import * as THREE from 'three';
import { eventBus, Events } from './EventBus.js';
import { gameState } from './GameState.js';
import {
  RENDER, CAMERA, COLORS, LIGHT, LIVES_START, SCORE, JUICE, PLAYER, KIDS,
} from './Constants.js';
import { makeMaterials } from '../level/VoxelFactory.js';
import { LevelBuilder, EDGE } from '../level/LevelBuilder.js';
import { Player } from '../gameplay/Player.js';
import { InputSystem } from '../systems/InputSystem.js';
import { CollisionSystem } from '../systems/CollisionSystem.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { AudioSystem } from '../systems/AudioSystem.js';
import { CameraRig } from '../systems/CameraRig.js';
import { DemoBot } from '../systems/DemoBot.js';
import { HUD } from '../ui/HUD.js';

const DEBRIS_COLORS = [COLORS.SHIRT, COLORS.SKIN, COLORS.TROUSERS, COLORS.HAIR, COLORS.SHOE]
  .map((c) => new THREE.Color(c));

export default class Game {
  constructor() {
    this.lastFrame = performance.now();
    this.deathTimer = 0;
    this.pendingLevel = 0;
    this.init();
  }

  init() {
    this.setupRenderer();
    this.setupScene();
    this.setupCamera();
    this.onWindowResize();
    this.setupSystems();
    this.setupUI();
    this.setupEventListeners();
    this.buildLevel();
    this.renderer.setAnimationLoop(() => this.animate());
  }

  // ── boot ────────────────────────────────────────────────────────────────

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setClearColor(RENDER.CLEAR_COLOR);
    this.renderer.localClippingEnabled = true;
    document.getElementById('game-container').appendChild(this.renderer.domElement);
    this.onWindowResize();
    this._onResize = () => this.onWindowResize();
    window.addEventListener('resize', this._onResize);
  }

  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(RENDER.CLEAR_COLOR);

    this.scene.add(new THREE.AmbientLight(COLORS.AMBIENT, LIGHT.AMBIENT_INTENSITY));
    const sun = new THREE.DirectionalLight(COLORS.SUN, LIGHT.SUN_INTENSITY);
    sun.position.set(...LIGHT.SUN_POS);
    this.scene.add(sun);

    // Rolling stock is cut off at the tunnel mouths instead of driving into view.
    this.clipPlanes = [
      new THREE.Plane(new THREE.Vector3(1, 0, 0), EDGE),
      new THREE.Plane(new THREE.Vector3(-1, 0, 0), EDGE),
    ];
    this.materials = makeMaterials(this.clipPlanes);
    this.materials.signalOn = new THREE.MeshBasicMaterial({ color: COLORS.SIGNAL_ON });
    this.materials.signalOff = new THREE.MeshLambertMaterial({ color: COLORS.SIGNAL_OFF });
  }

  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      CAMERA.FOV_LANDSCAPE,
      window.innerWidth / window.innerHeight,
      CAMERA.NEAR,
      CAMERA.FAR,
    );
    this.cameraRig = new CameraRig(this.camera);
    this.cameraRig.fit(window.innerWidth / window.innerHeight);
  }

  setupSystems() {
    this.levelBuilder = new LevelBuilder(this.scene, this.materials);
    this.player = new Player(this.scene, this.materials.scenery, this.materials.shadow);
    this.levelBuilder.setPlayer(this.player);
    this.collision = new CollisionSystem(this.player, this.levelBuilder.cobra);
    this.particles = new ParticleSystem(this.scene);
    this.audio = new AudioSystem();
    this.input = new InputSystem(this.renderer.domElement);
    this.demoBot = new DemoBot(this.player);
  }

  setupUI() {
    this.hud = new HUD();
  }

  setupEventListeners() {
    eventBus.on(Events.INPUT_START, () => this.onStartPressed());
    eventBus.on(Events.INPUT_PAUSE, () => this.togglePause());
    eventBus.on(Events.PLAYER_ADVANCE, () => this.onAdvance());
    eventBus.on(Events.PLAYER_LAND, ({ row }) => this.onLand(row));
    eventBus.on(Events.PLAYER_DIE, (d) => this.onDeath(d));
  }

  // ── flow ────────────────────────────────────────────────────────────────

  buildLevel() {
    this.particles?.clear();
    this.levelBuilder.build(gameState.run.level);
    gameState.resetRun(gameState.time);
    this.player.spawn();
    this.cameraRig.snap();
  }

  onStartPressed() {
    this.audio.ensure();

    if (gameState.game.isDemo) {
      gameState.game.isDemo = false;
      const best = gameState.run.best;
      gameState.reset();
      gameState.run.best = best;
      gameState.run.lives = LIVES_START;
      gameState.game.isDemo = false;
      gameState.run.childrenCount = Math.min(KIDS.MAX_COUNT ?? 10, KIDS.START_COUNT ?? 0);
      this.buildLevel();
      this.hud.sync();
    } else if (gameState.game.isPlaying) {
      return;
    } else if (gameState.game.over || !gameState.game.started) {
      const best = gameState.run.best;
      gameState.reset();
      gameState.run.best = best;
      gameState.run.lives = LIVES_START;
      gameState.game.isDemo = false;
      gameState.run.childrenCount = Math.min(KIDS.MAX_COUNT ?? 10, KIDS.START_COUNT ?? 0);
      this.buildLevel();
      this.hud.sync();
    }

    gameState.game.started = true;
    gameState.game.over = false;
    gameState.game.isPlaying = true;
    gameState.player.dead = false;
    gameState.player.invulnUntil = gameState.time + PLAYER.SPAWN_INVULN;
    gameState.level.startedAt = gameState.time;
    eventBus.emit(Events.GAME_START);
  }

  togglePause() {
    if (!gameState.game.isPlaying && !gameState.game.paused) return;
    gameState.game.paused = !gameState.game.paused;
    this.hud.showBanner(gameState.game.paused ? 'PAUSED' : 'GO');
  }

  onAdvance() {
    gameState.run.score += SCORE.ADVANCE;
    eventBus.emit(Events.SCORE_CHANGE);
  }

  onLand(row) {
    if (row === gameState.level.goalRow) this.completeLevel();
  }

  completeLevel() {
    if (!gameState.game.isPlaying) return;

    const intactKids = gameState.run.childrenCount || 0;
    const kidBonus = intactKids * (SCORE.KID_BONUS || 500);

    const bonus = SCORE.GOAL
      + SCORE.LEVEL_BONUS * gameState.run.level
      + gameState.timeBonus(gameState.time)
      + kidBonus;

    gameState.run.score += bonus;
    gameState.run.best = Math.max(gameState.run.best, gameState.run.score);

    // Add new kids for next level after calculating bonus
    const perCrossing = KIDS.PER_CROSSING ?? 1;
    const maxKids = KIDS.MAX_COUNT ?? 10;
    gameState.run.childrenCount = Math.min(maxKids, intactKids + perCrossing);

    eventBus.emit(Events.SCORE_CHANGE);
    eventBus.emit(Events.LEVEL_COMPLETE, {
      level: gameState.run.level,
      bonus,
      kidBonus,
      intactKids,
      children: gameState.run.childrenCount,
    });
    eventBus.emit(Events.FLASH);

    this.advanceLevel();
  }

  onDeath({ cause, x, row, lane, consist }) {
    gameState.run.lives -= 1;
    gameState.run.best = Math.max(gameState.run.best, gameState.run.score);
    this.player.triggerSmoosh(cause);
    eventBus.emit(Events.BURST, { x, row, cause, lane, consist });
    eventBus.emit(Events.SHAKE, JUICE.SHAKE_DEATH);
    eventBus.emit(Events.LIVES_CHANGE);
    this.deathTimer = JUICE.DEATH_FREEZE;
  }

  respawnOrEnd() {
    if (gameState.run.lives <= 0) {
      gameState.game.isPlaying = false;
      gameState.game.over = true;
      eventBus.emit(Events.GAME_OVER);
      return;
    }
    gameState.resetRun(gameState.time);
    gameState.player.invulnUntil = gameState.time + PLAYER.SPAWN_INVULN;
    this.player.spawn();
    this.cameraRig.snap();
    eventBus.emit(Events.PLAYER_RESPAWN);
  }

  advanceLevel() {
    gameState.run.level += 1;
    this.buildLevel();
    gameState.game.isPlaying = true;
    gameState.player.invulnUntil = gameState.time + PLAYER.SPAWN_INVULN;
    eventBus.emit(Events.LEVEL_CHANGE);
  }

  // ── loop ────────────────────────────────────────────────────────────────

  animate() {
    const now = performance.now();
    const dt = Math.min((now - this.lastFrame) / 1000, 0.1);
    this.lastFrame = now;
    const running = !gameState.game.paused;
    const step = running ? dt : 0;

    gameState.time += step;
    this.levelBuilder.update(step, gameState.time);

    if (gameState.game.isDemo) {
      if (gameState.player.dead) {
        this.deathTimer -= step;
        if (this.deathTimer <= 0) {
          gameState.player.dead = false;
          this.player.spawn();
          this.deathTimer = 0;
        }
      } else {
        this.demoBot.update(step);
      }
    } else {
      if (this.deathTimer > 0) {
        this.deathTimer -= step;
        if (this.deathTimer <= 0) this.respawnOrEnd();
      } else if (this.pendingLevel > 0) {
        this.pendingLevel -= step;
        if (this.pendingLevel <= 0) { this.pendingLevel = 0; this.advanceLevel(); }
      }
    }

    this.player.update(step);
    this.collision.update();
    this.particles.update(step);
    this.cameraRig.update(dt);

    this.renderer.render(this.scene, this.camera);
  }

  onWindowResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (this.cameraRig) this.cameraRig.fit(w / h);
    // Render at a fraction of the display size; CSS upscales with hard pixels.
    this.renderer.setPixelRatio(RENDER.MAX_PIXEL_RATIO);
    this.renderer.setSize(
      Math.max(160, Math.floor(w / RENDER.PIXEL_SCALE)),
      Math.max(120, Math.floor(h / RENDER.PIXEL_SCALE)),
      false,
    );
    const c = this.renderer.domElement;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
  }

  dispose() {
    this.renderer.setAnimationLoop(null);
    window.removeEventListener('resize', this._onResize);
    this.input.dispose();
    this.audio.dispose();
    this.particles.dispose();
    this.player.dispose();
    this.levelBuilder.dispose();
    this.cameraRig.dispose();
    this.hud.dispose();
    for (const m of Object.values(this.materials)) m.dispose?.();
    this.renderer.dispose();
    eventBus.clear();
  }
}
